import React, { useState, useEffect } from 'react'
import {
    ScoopAlert,
    ScoopButton,
    ScoopSpinner,
    ScoopText,
    ScoopSelector,
    ScoopTextField,
} from '../../../ui'
import { useBYOK } from '../../../../api/hooks/byok/useBYOK'
import { EVENTS, trackEvent } from '../../../../events'
import styles from './AIProviderSettings.module.scss'

const PROVIDER_OPTIONS = [
    { value: 'scoop', label: 'System Default (Scoop AI)' },
    { value: 'claude', label: 'Claude (Anthropic)' },
    { value: 'openai', label: 'OpenAI (GPT)' },
]

export const AIProviderSettings = ({ accountID }) => {
    const [provider, setProvider] = useState('scoop')
    const [apiKey, setApiKey] = useState('')
    const [testResult, setTestResult] = useState(null)
    const [saveResult, setSaveResult] = useState(null)

    const { settings, isLoading, updateSettings, isUpdating, testKey, isTesting } =
        useBYOK(accountID)

    // Load current settings
    useEffect(() => {
        if (settings) setProvider(settings.aiProvider || 'scoop')
    }, [settings])

    const handleTestKey = () => {
        if (provider === 'scoop' || !apiKey) {
            setTestResult({
                success: false,
                message: 'Please select a provider and enter an API key',
            })
            return
        }

        setTestResult(null)
        testKey(
            { provider, apiKey },
            {
                onSuccess: (data) => {
                    if (data.error) {
                        setTestResult({
                            success: false,
                            message: 'Test failed. Please try again.',
                        })
                    } else {
                        setTestResult({
                            success: data.success,
                            message: data.success ? data.message : data.errorMessage,
                        })
                    }
                },
                onError: (error) => {
                    setTestResult({
                        success: false,
                        message: error?.message || 'Test failed. Please try again.',
                    })
                },
            }
        )
    }

    const handleSave = () => {
        trackEvent(EVENTS.ACCOUNT.ADD_AI_PROVIDER, { provider })
        setSaveResult(null)
        setTestResult(null)

        updateSettings({
            aiProvider: provider === 'scoop' ? null : provider,
            aiApiKey: apiKey || null,
            onSuccess: (data) => {
                setSaveResult({
                    success: true,
                    message: data.message || 'Settings saved successfully',
                })
                // Clear API key field for security
                setApiKey('')
            },
            onError: (error) => {
                setSaveResult({
                    success: false,
                    message: error?.message || 'Failed to save settings',
                })
            },
        })
    }

    const handleClear = () => {
        setSaveResult(null)
        setTestResult(null)

        updateSettings({
            aiProvider: null,
            apiKey: null,
            onSuccess: (data) => {
                setProvider('scoop')
                setApiKey('')
                setSaveResult({
                    success: true,
                    message: 'BYOK settings cleared. Using system defaults.',
                })
            },
            onError: (error) => {
                setSaveResult({
                    success: false,
                    message: error?.message || 'Failed to clear settings',
                })
            },
        })
    }

    if (isLoading) {
        return (
            <div className={styles.container}>
                <ScoopSpinner size={40} />
            </div>
        )
    }

    const hasConfig = settings?.aiProvider && settings?.hasCustomKey
    const hasSavedProvider = Boolean(settings?.aiProvider)
    const isModified = provider !== (settings?.aiProvider || 'scoop') || apiKey !== ''

    return (
        <div className={styles.container}>
            {/* Current Status */}
            {hasConfig && !isModified && (
                <ScoopAlert severity="success">
                    BYOK is active: Using your {settings.aiProvider.toUpperCase()} API key
                </ScoopAlert>
            )}

            {/* Info Banner */}
            <ScoopAlert severity="info">
                <div className={styles.infoText}>
                    <ScoopText className={styles.infoTitle}>What is BYOK?</ScoopText>
                    <ScoopText className={styles.infoDescription}>
                        Bring Your Own Key lets you use your own AI provider API keys. Your usage
                        won't count against Scoop's limits, and you'll have full control over costs.
                    </ScoopText>
                </div>
            </ScoopAlert>

            {/* Provider Selection */}
            <div className={styles.field}>
                <ScoopSelector
                    value={provider}
                    label="AI Provider"
                    onChange={(e) => {
                        setProvider(e.target.value)
                        setTestResult(null)
                    }}
                    options={PROVIDER_OPTIONS}
                    size="sm"
                />
            </div>

            {/* API Key Input */}
            <div className={styles.apiKeyField}>
                <ScoopText className={styles.label}>
                    {settings?.hasCustomKey ? 'Change API Key' : 'API Key'}
                    {settings?.hasCustomKey && !apiKey && (
                        <span className={styles.keyStatus}> (Currently configured)</span>
                    )}
                </ScoopText>
                <div className={styles.apiKeyRow}>
                    <ScoopTextField
                        type="password"
                        value={apiKey}
                        onChange={(e) => {
                            setApiKey(e.target.value)
                            setTestResult(null)
                        }}
                        placeholder={
                            provider !== 'scoop'
                                ? settings?.hasCustomKey
                                    ? `Enter new ${provider === 'claude' ? 'Claude' : 'OpenAI'} API key`
                                    : `Enter your ${provider === 'claude' ? 'Claude' : 'OpenAI'} API key`
                                : 'Select a provider first'
                        }
                        disabled={provider === 'scoop'}
                        className={styles.input}
                    />
                    <ScoopButton
                        onClick={handleTestKey}
                        disabled={isTesting || provider === 'scoop' || !apiKey}
                        variant="outlined"
                        className={styles.testButton}
                    >
                        {isTesting ? 'Testing...' : 'Test API Key'}
                    </ScoopButton>
                </div>
                {provider !== 'scoop' && (
                    <ScoopText className={styles.hint}>
                        {provider === 'claude' && 'Get your API key from console.anthropic.com'}
                        {provider === 'openai' && 'Get your API key from platform.openai.com'}
                    </ScoopText>
                )}
            </div>

            {/* Test Result */}
            {testResult && (
                <ScoopAlert severity={testResult.success ? 'success' : 'error'}>
                    {testResult.message}
                </ScoopAlert>
            )}

            {/* Save Result */}
            {saveResult && (
                <ScoopAlert severity={saveResult.success ? 'success' : 'error'}>
                    {saveResult.message}
                </ScoopAlert>
            )}

            {/* Actions */}
            <div className={styles.actions}>
                <ScoopButton
                    onClick={handleSave}
                    disabled={isUpdating || !isModified || provider === 'scoop'}
                    variant="contained"
                    className={styles.saveButton}
                >
                    {isUpdating ? 'Saving...' : 'Save Settings'}
                </ScoopButton>

                <ScoopButton
                    onClick={handleClear}
                    disabled={isUpdating || !hasSavedProvider}
                    variant="outlined"
                    className={styles.clearButton}
                >
                    Clear & Use System Default
                </ScoopButton>
            </div>
        </div>
    )
}
